import React, { useState } from "react";
import { useUsers } from "../../contexts/UsersContext";

const UserForm = ({ closeModal, onSubmit, initialValues }) => {
  const { users } = useUsers(); // Fetch users from context
  const [formData, setFormData] = useState(
    initialValues || {
      name: "",
      email: "",
      manager_id: "", 
      password: "",
      password_confirmation: "",
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Name: only letters and spaces allowed, min 2 characters
    if (!/^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(formData.name)) {
      alert("Name must contain only letters and spaces in between words (no leading or trailing spaces).");
      return;
    }
    
    // Email format validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      alert("Invalid email format.");
      return;
    }
    
    // Only validate password fields if creating OR editing with new password
    const isEditing = Boolean(initialValues);
    const isPasswordEntered = formData.password || formData.password_confirmation;

    if (!isEditing || isPasswordEntered) {
      // Password: at least 8 chars, one uppercase
      if (!/^(?=.*[A-Z]).{8,}$/.test(formData.password)) {
        alert("Password must be at least 8 characters and contain one uppercase letter.");
        return;
      }

      // Password and confirmation match
      if (formData.password !== formData.password_confirmation) {
        alert("Passwords do not match.");
        return;
      }
    }
    
    onSubmit(formData); // Pass the form data to the parent component
    // closeModal(); // Close the modal
  };

  // Filter users to exclude the current user (if editing) from being assigned as their own manager
  const managerOptions = users.filter(
    (user) => !initialValues || user.id !== initialValues.id
  );

  return (
    <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4 font-poppins">
      {/* Full Name Field */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-white">
          Full Name <span className='text-red-500'>*</span>
        </label>
        <input
          type="text"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          className="mt-1 block w-full p-2 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
          required
        />
      </div>

      {/* Email Field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-white">
          Email <span className='text-red-500'>*</span>
        </label>
        <input
          type="email"
          name="email"
          id="email"
          autoComplete="off"
          value={formData.email}
          onChange={handleChange}
          className="mt-1 block w-full p-2 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
          required
        />
      </div>

      {/* Manager Dropdown */}
      <div>
        <label htmlFor="manager_id" className="block text-sm font-medium text-gray-700 dark:text-white">
          Manager 
        </label>
        <select
          name="manager_id"
          id="manager_id"
          value={formData.manager_id}
          onChange={handleChange}
          className="mt-1 block w-full p-2 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
        >
          <option value="">None</option>
          {managerOptions.map((manager) => (
            <option key={manager.id} value={manager.id}>
              {manager.name} ({manager.email})
            </option>
          ))}
        </select>
      </div>

      {/* Password Fields - Only for Adding New Users */}
      {!initialValues && (
        <>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-white">
              Password <span className='text-red-500'>*</span>
            </label>
            <input
              type="password"
              name="password"
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              className="mt-1 block w-full p-2 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
              required
            />
          </div>

          <div>
            <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 dark:text-white">
              Confirm Password <span className='text-red-500'>*</span>
            </label>
            <input
              type="password"
              name="password_confirmation"
              id="password_confirmation"
              value={formData.password_confirmation}
              onChange={handleChange}
              className="mt-1 block w-full p-2 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
              required
            />
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end items-center gap-2">
        <button
          type="button"
          onClick={closeModal}
          className="mt-1 px-8 py-2 hover:bg-gray-200 font-medium dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-8 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700"
        >
          {initialValues ? "Update" : "Add User"}
        </button>
      </div>
    </form>
  );
};

export default UserForm;
